import styles from "./styles.module.scss"
import { useStores } from "../../../models/helpers/useStores"
import { _t } from "@cruisen/i18n"
import UserAvatar from "../../atoms/UserAvatar"
import { timestampToTime } from "@cruisen/react-shared"
import { observer } from "mobx-react-lite"
import { NavigatorFile, UserID } from "@cruisen/contract"
import classNames from "classnames"
import { UserMessageFile } from "./UserMessageFile"
import Linkify from "react-linkify"

type AuthorDetails = {
  isSupport?: boolean
  userId?: UserID
  firstName: string
  lastName: string
  avatar: string
}

const UserMessageHeading = observer(
  ({
    authorDetails,
    isCurrentUserMessage,
    timeStamp,
  }: {
    authorDetails: AuthorDetails
    isCurrentUserMessage: boolean
    timeStamp: number
  }) => {
    const { activeTrip } = useStores()

    const collaboratorUserData =
      activeTrip.collaborators.find(
        (collaborator) => collaborator.userId === authorDetails.userId,
      ) || authorDetails

    if (isCurrentUserMessage) {
      return (
        <div className={classNames(styles.userMessageHeading, styles.isCurrentUserMessage)}>
          <p className={styles.timeStamp}>{timestampToTime(timeStamp)}</p>
          <UserAvatar
            nameInitals={`${collaboratorUserData.firstName?.[0]}${collaboratorUserData.lastName?.[0]}`}
            avatar={collaboratorUserData.avatar}
            size={"sm"}
          />
        </div>
      )
    }
    return (
      <div className={styles.userMessageHeading}>
        <UserAvatar
          nameInitals={`${collaboratorUserData.firstName?.[0]}${collaboratorUserData.lastName?.[0]}`}
          avatar={collaboratorUserData.avatar}
          size={"sm"}
          isPrimary={false}
        />
        <div className={styles.headingColumn}>
          <div className={styles.headingRow}>
            <p className={styles.messageFrom}>
              {authorDetails.firstName} {authorDetails.lastName[0]}.
            </p>
            <p className={styles.timeStamp}>{timestampToTime(timeStamp)}</p>
          </div>
          {collaboratorUserData.isSupport && (
            <p className={styles.fromCruisen}>{_t("navigator.fromCruisen")}</p>
          )}
        </div>
      </div>
    )
  },
)

export const UserMessage = ({
  message,
  showAvatarAndName,
  authorDetails,
  timeStamp,
  fileData,
}: {
  message: string
  showAvatarAndName: boolean
  authorDetails: AuthorDetails
  timeStamp: number
  fileData?: NavigatorFile
}) => {
  const { loggedInUser } = useStores()

  const isCurrentUserMessage = authorDetails.userId === loggedInUser.id

  return (
    <div className={showAvatarAndName ? styles.containerWithMargin : styles.container}>
      {showAvatarAndName && (
        <UserMessageHeading
          authorDetails={authorDetails}
          isCurrentUserMessage={isCurrentUserMessage}
          timeStamp={timeStamp}
        />
      )}

      {isCurrentUserMessage ? (
        <div className={classNames(styles.flexContainer, styles.flexContainerEnd)}>
          {message ? (
            <div
              className={classNames([
                styles.message,
                styles.primaryBackground,
                styles.roundedTopLeftCorner,
                showAvatarAndName ? "" : styles.roundedTopRightCorner,
                showAvatarAndName ? styles.negativeMargin : styles.marginTop,
              ])}
            >
              <p className={styles.messageText}>
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                  )}
                  data-testid="current-user-message"
                >
                  {message}
                </Linkify>
              </p>
            </div>
          ) : null}
          {fileData ? <UserMessageFile file={fileData} /> : null}
        </div>
      ) : (
        <div className={classNames(styles.flexContainer, styles.flexContainerStart)}>
          {message ? (
            <div
              className={classNames([
                styles.message,
                styles.grayBackground,
                styles.roundedTopRightCorner,
                showAvatarAndName ? "" : styles.roundedTopLeftCorner,
                showAvatarAndName ? styles.negativeMargin : styles.marginTop,
              ])}
            >
              <p className={styles.messageText}>
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                  )}
                >
                  {message}
                </Linkify>
              </p>
            </div>
          ) : null}
          {fileData ? <UserMessageFile file={fileData} /> : null}
        </div>
      )}
    </div>
  )
}
